import React, { useState, lazy } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Testimonial {
  name: string;
  text: string;
  image: string;
}
// Fotos reales de viajeras (en su mayoría mujeres latinas/adultas), con
// luz natural, ropa cotidiana de viaje y expresiones tranquilas. Formato
// vertical/rectangular para que cada card parezca una reseña real.
const TESTIMONIALS: Testimonial[] = [{
  name: 'Florencia R.',
  text: 'Compré la guía porque tenía un viaje familiar y ya venía con la cabeza a mil desde semanas antes. Me ayudó mucho tener todo ordenado: qué hacer antes del vuelo, qué escuchar y qué leer si me agarraba ansiedad. Sentí que no iba tan “a la deriva”.',
  image: 'https://cdn.magicpatterns.com/uploads/pxeqdKwt8MpfWpHoEkq4pc/image.png'
}, {
  name: 'Valentina G.',
  text: 'Lo que más miedo me daba era el despegue. Me sirvió tener frases concretas y entender que varias sensaciones que me asustaban eran normales. No fue que se me fue el miedo de golpe, pero viajé mucho más tranquila.',
  image: 'https://cdn.magicpatterns.com/uploads/wt6kRRJvbdReNQbnLH3gi1/image.png'
}, {
  name: 'Julieta M.',
  text: 'Me gustó que no te habla como si fueras exagerada. Te entiende. Yo venía re nerviosa por un vuelo largo y los audios me ayudaron un montón, sobre todo en la espera del aeropuerto y cuando empezó a moverse un poco el avión.',
  image: 'https://cdn.magicpatterns.com/uploads/u1C6psEsNqjMYyqsKWhiHy/image.png'
}, {
  name: 'Agustina F.',
  text: 'Yo sufro más antes de viajar que durante el vuelo. La noche anterior hice una de las meditaciones y completé unos ejercicios del cuaderno. Me ayudó a sacar de la cabeza un montón de pensamientos que me estaban dando vueltas.',
  image: 'https://cdn.magicpatterns.com/uploads/pKvfQc7ERmJkENcVNo4HKw/image.png'
}, {
  name: 'Diego S.',
  text: 'No soy muy de estas cosas, pero lo compré porque tenía que volar por trabajo y venía esquivándolo. Me sirvió mucho entender los ruidos del avión y tener los audios a mano. No desapareció todo el miedo, pero pude viajar sin pasarla tan mal.',
  image: 'https://cdn.magicpatterns.com/uploads/e19UQu84twb4jAd4mTtQnq/image.png'
}];
export function TestimonialCarousel() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrent(prev => (prev + newDirection + TESTIMONIALS.length) % TESTIMONIALS.length);
  };
  const goTo = (index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  // Swipe handlers
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      paginate(1);
    } else if (isRightSwipe) {
      paginate(-1);
    }
    setTouchStart(0);
    setTouchEnd(0);
  };
  const t = TESTIMONIALS[current];
  return <div className="relative max-w-4xl mx-auto" data-id="element-12" data-mp="yuzx1uzmtm6i">
      <div className="relative overflow-hidden min-h-[34rem] md:min-h-[26rem] flex items-center" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-13" data-mp="b3uf9aa4sjtk">
        <AnimatePresence mode="wait" custom={direction} data-id="element-14" data-mp="kuytu8vwa2nv">
          <motion.div key={current} custom={direction} initial={{
          opacity: 0,
          x: direction >= 0 ? 60 : -60
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: direction >= 0 ? -60 : 60
        }} transition={{
          duration: 0.35,
          ease: 'easeInOut'
        }} className="w-full px-2" data-id="element-15" data-mp="kvrvivfzy1z5">
            <div className="bg-white rounded-3xl shadow-sm border border-travertino/20 overflow-hidden flex flex-col md:flex-row items-stretch" data-id="element-16" data-mp="2mun3alhxuczc">
              {/* Foto real de la viajera, formato rectangular */}
              <div className="w-full h-56 md:w-56 md:h-auto md:min-h-[24rem] shrink-0" data-id="element-17" data-mp="2vsflbxf1nyqf">
                <img src={t.image} alt={`Foto de ${t.name}`} className="w-full h-full object-cover" loading="lazy" data-id="element-18" data-mp="30m1motfrcg6x" />
              </div>
              <div className="flex-1 flex flex-col justify-center p-6 md:p-10 text-center md:text-left" data-id="element-19" data-mp="3aimtnkbc81de">
                <div className="flex justify-center md:justify-start gap-1 mb-3 md:mb-4" aria-label="5 de 5 estrellas" data-id="element-20" data-mp="3ekht0j9pk4oj">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="text-[#D9B97C] fill-[#D9B97C]" size={18} data-id="element-21" data-mp="3jit2gsw3cd68" />)}
                </div>
                <p className="text-base md:text-xl text-oceano leading-relaxed italic mb-5 md:mb-6" data-id="element-22" data-mp="1k81opo0h30mb">
                  “{t.text}”
                </p>
                <p className="font-semibold text-oceano text-base md:text-lg" data-id="element-23" data-mp="1hmatbzkuc7c4">
                  {t.name}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <button onClick={() => paginate(-1)} aria-label="Testimonio anterior" className="hidden md:flex absolute top-1/2 -left-4 md:-left-6 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center text-caribe hover:bg-caribe hover:text-polar transition-colors border border-travertino/20" data-id="element-24" data-mp="17x0ywd2iycku">
        <ChevronLeft size={28} data-id="element-25" data-mp="1vm3elfdllvcp" />
      </button>
      <button onClick={() => paginate(1)} aria-label="Testimonio siguiente" className="hidden md:flex absolute top-1/2 -right-4 md:-right-6 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center text-caribe hover:bg-caribe hover:text-polar transition-colors border border-travertino/20" data-id="element-26" data-mp="1qm7c5kkjubz9">
        <ChevronRight size={28} data-id="element-27" data-mp="b5gv0qs8jr3c" />
      </button>

      <div className="flex justify-center gap-2 md:gap-3 mt-4 md:mt-8" data-id="element-28" data-mp="b7te579qc7hz">
        {TESTIMONIALS.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Ir al testimonio ${i + 1}`} className={`h-2 md:h-2.5 rounded-full transition-all ${i === current ? 'bg-caribe w-6 md:w-8' : 'bg-travertino/40 w-2 md:w-2.5'}`} data-id="element-29" data-mp="ckt64y8rhhhp" data-mpi={i} />)}
      </div>
    </div>;
}